import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { sizes } from '../utils/breakpoints'
import { useMediaQuery } from 'react-responsive'
import { useTranslation } from 'react-i18next'
import MenuImage from '../assets/images/menu.svg'
import TartisDesktop from '../assets/images/Logo_desktop.svg'
import TartisMobile from '../assets/images/Logo_mobile.svg'
import { useNavigate, useLocation } from 'react-router-dom'
import { useThemeMode } from '../context/ThemeModeContext'
import {
  getLangFromPath,
  stripLangPrefix,
  withLang,
  isLegalOnlyPath,
} from '../utils/i18nRoutes'

const NAV_PATHS = ['/servicios', '/sobre-nosotros', '/contacto', '/talento']

const NavBarComponent = () => {
  const isMobile = useMediaQuery({ maxWidth: sizes.mobile })
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { mode, toggleMode } = useThemeMode()

  const lang = getLangFromPath(location.pathname)
  const basePath = stripLangPrefix(location.pathname)

  const navItems = [
    { label: t('nav.servicios'), path: '/servicios' },
    { label: t('nav.sobreNosotros'), path: '/sobre-nosotros' },
    { label: t('nav.contacto'), path: '/contacto' },
    { label: t('nav.talento'), path: '/talento' },
  ]

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const goTo = (path) => {
    navigate(withLang(path, lang))
    setIsMenuOpen(false)
  }

  const switchLang = (nextLang) => {
    if (nextLang === lang) return
    const target = isLegalOnlyPath(location.pathname)
      ? withLang('/', nextLang)
      : withLang(basePath, nextLang)
    navigate(target)
    setIsMenuOpen(false)
  }

  const logo = isMobile ? TartisMobile : TartisDesktop

  const renderItems = () => (
    <>
      {navItems.map((item) => (
        <Section
          key={item.path}
          onClick={() => goTo(item.path)}
          $isActive={basePath === item.path && NAV_PATHS.includes(basePath)}
        >
          {item.label}
        </Section>
      ))}
      <LangSwitcher>
        <LangButton $active={lang === 'es'} onClick={() => switchLang('es')}>
          ES
        </LangButton>
        <LangDivider>/</LangDivider>
        <LangButton $active={lang === 'en'} onClick={() => switchLang('en')}>
          EN
        </LangButton>
      </LangSwitcher>
      <ThemeToggle
        type="button"
        onClick={toggleMode}
        aria-label={mode === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
      >
        {mode === 'dark' ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.6" />
            <path
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"
            />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z"
            />
          </svg>
        )}
      </ThemeToggle>
    </>
  )

  return (
    <Container>
      <InnerContainer>
        <TitleLogo onClick={() => goTo('/')}>
          <img src={logo} alt="" />
        </TitleLogo>
        {isMobile ? (
          <MenuActions onClick={handleMenuClick} aria-label="" />
        ) : (
          <Menu>{renderItems()}</Menu>
        )}
      </InnerContainer>
      {isMobile && isMenuOpen && (
        <MobileMenu ref={menuRef}>{renderItems()}</MobileMenu>
      )}
    </Container>
  )
}

export default NavBarComponent

const Container = styled.div`
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 10;
  background-color: ${({ theme }) => theme.colors.background.navbar};
  box-shadow: ${({ theme }) => theme.shadows.navbar};
  transition: background-color 0.2s ease;
`

const InnerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
  padding: 0 80px;
  box-sizing: border-box;

  @media (max-width: ${sizes.mobile}px) {
    height: 50px;
    padding: 0 16px;
  }
`

const TitleLogo = styled.div`
  margin-bottom: 0px !important;
  cursor: pointer;

  img {
    filter: ${({ theme }) => (theme.mode === 'dark' ? 'invert(1)' : 'none')};
  }

  @media (max-width: ${sizes.mobile}px) {
  }
`

const Menu = styled.div`
  display: flex;
  align-items: center;
  gap: 56px;
`

const Section = styled.button`
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.text.navActive : theme.colors.text.primary};
  font-family: ${({ $isActive, theme }) =>
    $isActive ? theme.fonts.headingAlt : theme.fonts.body};
  font-size: 18px;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    font-family: ${({ theme }) => theme.fonts.headingAlt};
  }

  @media (max-width: ${sizes.mobile}px) {
    font-size: 14px;
    line-height: 16.8px;
    width: 100%;
    text-align: center;

    &:hover {
      font-family: ${({ theme }) => theme.fonts.headingAlt};
      font-size: 16px;
    }
  }
`

const LangSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: ${sizes.mobile}px) {
    margin-top: 8px;
  }
`

const LangButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.primary : theme.colors.text.muted};
  font-family: ${({ $active, theme }) =>
    $active ? theme.fonts.accentBold : theme.fonts.accentRegular};
`

const LangDivider = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
`

const ThemeToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-left: 4px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: ${sizes.mobile}px) {
    margin-left: 0;
    margin-top: 4px;
  }
`

const MenuActions = styled.button`
  width: 30px;
  height: 30px;
  background-image: url(${MenuImage});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  border: none;
  background-color: transparent;
  cursor: pointer;
  filter: ${({ theme }) => (theme.mode === 'dark' ? 'invert(1)' : 'none')};

  display: flex;
  align-items: center;
  justify-content: center;
`

const MobileMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.page};
  box-shadow: ${({ theme }) => theme.shadows.navbar};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  gap: 20px;
  z-index: 9;
`
